import { BodyEmailTemplate, CanonicalLoad, stripExternalIdPrefix, sylectusStopDate } from '@numeo/types-core'
import dayjs from 'dayjs'
import { formatPickupDate } from '@/utils/format-date'
import { escapeHtmlAttr, sanitizeImageUrl } from '@/components/tiptap/utils'

interface EmailContent {
    subject: string
    body: string
}

/**
 * Full user/company data exposed to template variables.
 * Sourced from the selected Nylas email's companyDetail in the load-hub email selector.
 */
export interface UserCompanyData {
    companyName?: string
    mcNumber?: string
    dotNumber?: string
    phoneNumber?: string
    transferNumber?: string
    aiAgentName?: string
    companyAddress?: string
    details?: string
    userName?: string
    /** The SENDER's own address (the picked "From"), not a company field — templates that print a
     *  dispatcher contact block need it and nothing else carried it. */
    userEmail?: string
    /** The company logo's URL, lifted out of the saved signature. Only templates that carry their own
     *  footer need it; the signature block renders its own copy. */
    companyLogoUrl?: string
}

/**
 * Build the user-side variable map that is shared across all template interpolations.
 * These variables reference the CURRENTLY SELECTED email's companyDetail (from the load-hub selector).
 */
const buildUserReplacements = (user?: UserCompanyData): Record<string, string> => ({
    // Primary names
    '{{company}}': user?.companyName || '',
    '{{mc}}': user?.mcNumber || '',
    '{{dot}}': user?.dotNumber || '',
    '{{phone}}': user?.phoneNumber || '',
    '{{transfer}}': user?.transferNumber || '',
    '{{agent_name}}': user?.aiAgentName || '',
    '{{address}}': user?.companyAddress || '',
    '{{notes}}': user?.details || '',
    '{{user_name}}': user?.userName || '',
    '{{user_email}}': user?.userEmail || '',
    // A whole <img>, not a bare URL: a template has no conditionals, so an operator with no logo would
    // otherwise render `<img src="">` and show a broken-image icon. Empty string collapses cleanly.
    '{{company_logo}}': user?.companyLogoUrl
        ? `<img src="${escapeHtmlAttr(sanitizeImageUrl(user.companyLogoUrl))}" alt="" style="display:block;margin:0 auto 6px;width:90px;max-width:100%;" />`
        : '',
    // Explicit `user_*` aliases for unambiguous use in templates
    '{{user_company}}': user?.companyName || '',
    '{{user_mc}}': user?.mcNumber || '',
    '{{user_dot}}': user?.dotNumber || '',
    '{{user_phone}}': user?.phoneNumber || '',
    '{{user_transfer}}': user?.transferNumber || '',
    '{{user_agent}}': user?.aiAgentName || '',
    '{{user_address}}': user?.companyAddress || '',
})

/** Normalize a load/email rate at the boundary where it becomes broker-facing copy. */
export const getPositiveEmailRate = (value: number | string | null | undefined): number | null => {
    const rate = Number(value)
    return Number.isFinite(rate) && rate > 0 ? rate : null
}

const formatEmailRate = (rate: number | null): string => (rate == null ? '-' : rate.toLocaleString('en-US'))

const RATE_CONTEXT_LENGTH = 80
/** How much surrounding text a rate slot beyond the first must share with the edited draft before it is
 *  rewritten — enough that a hand-typed number elsewhere (a fuel advance, a weight) is never mistaken for it. */
const MIN_EXTRA_SLOT_CONTEXT = 6

/** The text of HTML — each tag a space, whitespace collapsed — and where each character sits in the HTML. Slots
 *  are matched on what the reader sees, never on markup: the editor re-serialises tags, and attributes are full
 *  of numbers. The space keeps `$1,000</p><p>Dimensions` two words. */
interface TextProjection {
    text: string
    htmlIndex: number[]
}

const projectText = (content: string): TextProjection => {
    let text = ''
    const htmlIndex: number[] = []
    let inTag = false
    for (let index = 0; index < content.length; index += 1) {
        const char = content[index]
        const afterSpace = text === '' || /\s$/.test(text)
        if (char === '<') {
            inTag = true
            if (!afterSpace) {
                text += ' '
                htmlIndex.push(index)
            }
        }
        // Whitespace collapses, as it does on screen — the editor and the template rarely space markup alike.
        if (!inTag && !(afterSpace && /\s/.test(char))) {
            text += char
            htmlIndex.push(index)
        }
        if (char === '>') inTag = false
    }
    return { text, htmlIndex }
}

interface RateSlot {
    /** Position in the projected text. */
    index: number
    value: string
    dollar: boolean
}

/** A bid no real load carries. Rendering a template with it marks exactly where `{{rate}}` lands — the only way to
 *  tell a rate slot from another field that happens to show the same number (a 1,000 lb load bid at $1,000). */
export const RATE_SLOT_MARKER = 987_654_321

const slotAt = (text: string, index: number, value: string): RateSlot => ({ index, value, dollar: text[index - 1] === '$' })

/** The generated text at the current rate, and where each of its rate slots sits — read off the marker render. */
const generatedRateSlots = (rateLayout: string, rateText: string): { text: string; slots: RateSlot[] } => {
    const parts = projectText(rateLayout).text.split(formatEmailRate(RATE_SLOT_MARKER))
    let text = ''
    const slots: RateSlot[] = []
    parts.forEach((part, index) => {
        text += part
        if (index === parts.length - 1) return
        slots.push(slotAt(text, text.length, rateText))
        text += rateText
    })
    return { text, slots }
}

/** Every value in the draft that could be a rate slot: an amount, or the `-` an empty rate renders as. */
const draftRateCandidates = (text: string): RateSlot[] =>
    Array.from(text.matchAll(/(?<![\w.,-])(?:-|\d+(?:,\d{3})*(?:\.\d+)?)(?![\w-]|[.,]\d)/g), (match) => slotAt(text, match.index, match[0]))

const commonPrefixLength = (left: string, right: string): number => {
    const limit = Math.min(left.length, right.length)
    let length = 0
    while (length < limit && left[length] === right[length]) length += 1
    return length
}

const commonSuffixLength = (left: string, right: string): number => commonPrefixLength([...left].reverse().join(''), [...right].reverse().join(''))

const contextScore = (draft: string, draftSlot: RateSlot, generated: string, generatedSlot: RateSlot): number => {
    const draftLeft = draft.slice(Math.max(0, draftSlot.index - RATE_CONTEXT_LENGTH), draftSlot.index)
    const generatedLeft = generated.slice(Math.max(0, generatedSlot.index - RATE_CONTEXT_LENGTH), generatedSlot.index)
    const draftRight = draft.slice(draftSlot.index + draftSlot.value.length, draftSlot.index + draftSlot.value.length + RATE_CONTEXT_LENGTH)
    const generatedRight = generated.slice(
        generatedSlot.index + generatedSlot.value.length,
        generatedSlot.index + generatedSlot.value.length + RATE_CONTEXT_LENGTH
    )
    return commonSuffixLength(draftLeft, generatedLeft) + commonPrefixLength(draftRight, generatedRight)
}

/**
 * Bring an edited draft's rate up to the current bid without touching the dispatcher's other edits.
 *
 * `rateLayout` is the content rendered at RATE_SLOT_MARKER, which shows where the template put `{{rate}}` —
 * possibly several times, with or without a `$`. Each of those slots is matched to the draft value whose surrounding text agrees with it most,
 * and only those values are rewritten. A `$` slot only matches a `$` value, a bare slot a bare value. The best
 * `$` match always applies, as it always has — it is the price the broker is quoted. Any further slot must share
 * real context with its match (MIN_EXTRA_SLOT_CONTEXT), so a slot the dispatcher deleted cannot take over some
 * other number of theirs.
 */
export const synchronizeEmailTemplateRate = (draft: string, rateLayout: string, rate: number | string | null | undefined): string => {
    const currentRate = getPositiveEmailRate(rate)
    if (currentRate == null) return draft
    const rateText = formatEmailRate(currentRate)
    const { text: generated, slots } = generatedRateSlots(rateLayout, rateText)
    const draftProjection = projectText(draft)
    const candidates = draftRateCandidates(draftProjection.text)

    const pairs = slots
        .flatMap((slot, slotIndex) =>
            candidates
                .map((candidate, candidateIndex) => ({
                    slotIndex,
                    candidateIndex,
                    candidate,
                    dollar: slot.dollar,
                    score: contextScore(draftProjection.text, candidate, generated, slot),
                }))
                .filter((pair) => pair.candidate.dollar === pair.dollar)
        )
        .sort((left, right) => right.score - left.score)

    const usedSlots = new Set<number>()
    const usedCandidates = new Set<number>()
    const replaced: RateSlot[] = []
    for (const pair of pairs) {
        if (usedSlots.has(pair.slotIndex) || usedCandidates.has(pair.candidateIndex)) continue
        const firstPrice = pair.dollar && !replaced.some((slot) => slot.dollar)
        if (!firstPrice && pair.score < MIN_EXTRA_SLOT_CONTEXT) continue
        usedSlots.add(pair.slotIndex)
        usedCandidates.add(pair.candidateIndex)
        replaced.push(pair.candidate)
    }

    // From the end, so earlier HTML positions stay valid.
    const synced = replaced
        .sort((left, right) => right.index - left.index)
        .reduce((content, slot) => {
            const start = draftProjection.htmlIndex[slot.index]
            const end = draftProjection.htmlIndex[slot.index + slot.value.length - 1] + 1
            return `${content.slice(0, start)}${rateText}${content.slice(end)}`
        }, draft)
    return fillMissingEmailRate(synced, currentRate)
}

/**
 * Reconcile the unresolved rate marker in an edited draft with the current calculator value.
 * Untouched drafts are regenerated at send time, but edited drafts intentionally keep their copy;
 * replacing only `$-` preserves those edits while preventing a populated bid from being sent blank.
 */
export const fillMissingEmailRate = (content: string, rate: number | string | null | undefined): string => {
    const currentRate = getPositiveEmailRate(rate)
    if (currentRate == null) return content
    return content.split('$-').join(`$${formatEmailRate(currentRate)}`)
}

interface PrepareEmailContentForSendParams {
    draftSubject: string
    draftBody: string
    currentSubject: string
    currentBody: string
    subjectEdited: boolean
    bodyEdited: boolean
    rate: number | string | null | undefined
    /** The current subject and body rendered at RATE_SLOT_MARKER. Given → every rate slot follows `rate` (Box
     *  Truck's bid); omitted → only an unresolved `$-` is filled. */
    rateLayout?: EmailContent
}

/** Select current generated content unless the dispatcher edited it, then reconcile its rate marker. */
export const prepareEmailContentForSend = ({
    draftSubject,
    draftBody,
    currentSubject,
    currentBody,
    subjectEdited,
    bodyEdited,
    rate,
    rateLayout,
}: PrepareEmailContentForSendParams): EmailContent => {
    const selectedSubject = subjectEdited ? draftSubject : currentSubject
    const selectedBody = bodyEdited ? draftBody : currentBody
    return {
        subject: rateLayout ? synchronizeEmailTemplateRate(selectedSubject, rateLayout.subject, rate) : fillMissingEmailRate(selectedSubject, rate),
        body: rateLayout ? synchronizeEmailTemplateRate(selectedBody, rateLayout.body, rate) : fillMissingEmailRate(selectedBody, rate),
    }
}

/**
 * Apply template variable interpolation to a string
 * Supports variables like {{origin_city}}, {{destination_city}}, etc.
 */
export const applyTemplateVariables = (template: string, load: CanonicalLoad, userCompanyData?: UserCompanyData): string => {
    const rate = getPositiveEmailRate(load.rateAmountUsd) ?? 0
    const rpm = rate && load.distanceMiles ? (rate / Number(load.distanceMiles)).toFixed(2) : '0.00'
    const origin = [load.pickupCity, load.pickupStateProv].filter(Boolean).join(', ')
    const destination = [load.deliveryCity, load.deliveryStateProv].filter(Boolean).join(', ')
    // Upstream pickup dates arrive as UTC midnight (date-only) — parsing them in the viewer's local zone with
    // dayjs renders the day BEFORE for negative-offset timezones (a DAT "Aug 14" load emailed "on 08/13/2026").
    // Route through the shared formatPickupDate with parseAs:'utc', matching what the load row displays.
    //
    // A Sylectus post dates its pickup in the POSTER'S zone, so its own line wins: an evening Eastern pickup
    // is a different CALENDAR DAY once its instant is read anywhere else, and quoting that shifted day back
    // to the very broker who posted it is the worst place for this to surface. Null for every other source
    // (and for "ASAP" text), which keeps the UTC-parsed path exactly as it was.
    const pickupDate =
        sylectusStopDate(load.details?.sylectusPickupText) ||
        formatPickupDate(load.pickupDateTime ?? load.pickupWindowStart, undefined, { format: 'MM/DD/YYYY', parseAs: 'utc' }) ||
        dayjs().format('MM/DD/YYYY')
    const dotLine = userCompanyData?.dotNumber ? ` · DOT: ${userCompanyData.dotNumber}` : ''
    const phoneLine = userCompanyData?.phoneNumber ? `Phone: ${userCompanyData.phoneNumber}` : ''

    const replacements: Record<string, string> = {
        '{{broker_contact_name}}': load.brokerContactName || 'Team',
        '{{origin}}': origin,
        '{{destination}}': destination,
        '{{origin_city}}': load.pickupCity || '',
        '{{origin_state}}': load.pickupStateProv || '',
        '{{destination_city}}': load.deliveryCity || '',
        '{{destination_state}}': load.deliveryStateProv || '',
        // Friendly load id the UI shows (Convoy id / broker load number). Fall back to the raw external id
        // with the `SOURCE|` prefix stripped — never the composite dedup key (e.g. `ZS1cYVwG`, not `DAT|ZS1cYVwG`).
        '{{load_id}}': load.loadID?.toString() || stripExternalIdPrefix(load.externalId) || 'N/A',
        '{{equipment}}': load.equipmentType || '',
        '{{equipment_type}}': load.equipmentType || 'N/A',
        '{{total_mileage}}': load.distanceMiles?.toString() || '0',
        '{{trip_miles}}': load.distanceMiles ? String(Math.round(Number(load.distanceMiles))) : '0',
        // Deadhead-to-pickup lives on `details.origin_deadhead_miles` (populated by search enrichment /
        // map calc) — the same field the Trip section renders. It's null until surfaced, so fall back to 0.
        '{{deadhead_mileage}}': load.details?.origin_deadhead_miles != null ? String(Math.round(load.details.origin_deadhead_miles)) : '0',
        '{{deadhead}}': load.details?.origin_deadhead_miles != null ? String(Math.round(load.details.origin_deadhead_miles)) : '0',
        '{{total_miles}}': load.distanceMiles?.toString() || '0',
        // Legacy alias for the pickup date, kept for templates written before {{pickup_date}} existed. Same
        // source-zone rule: the poster's own date wins, so the two variables can never quote the broker
        // different days for one load.
        '{{date}}':
            sylectusStopDate(load.details?.sylectusPickupText) ||
            (load.pickupDateTime ? dayjs(load.pickupDateTime).format('MM/DD/YYYY') : dayjs().format('MM/DD/YYYY')),
        '{{pickup_date}}': pickupDate,
        '{{rate}}': formatEmailRate(rate || null),
        '{{total_rate}}': load.rateAmountUsd?.toString() || 'N/A',
        '{{rpm}}': rpm,
        '{{weight}}': load.weightLbs ? load.weightLbs.toLocaleString() : '-',
        '{{length}}': load.equipmentLengthFt ? String(load.equipmentLengthFt) : '',
        '{{load_type}}': load.fullPartial || '',
        // Conditional signature lines (empty when the field is missing so no stray separators remain)
        '{{dot_line}}': dotLine,
        '{{phone_line}}': phoneLine,
        // Broker aliases — {{company_name}} and {{mc_number}} historically refer to the broker
        '{{company_name}}': load.brokerCompanyName || 'N/A',
        '{{mc_number}}': load.brokerMc || 'N/A',
        '{{broker_company}}': load.brokerCompanyName || '',
        '{{broker_mc}}': load.brokerMc || '',
        // The address this email is ADDRESSED to, resolved the same way the compose resolves its recipient
        // (`brokerContactEmail` then `posterContactEmail` — also what Box Truck's `brokerEmail()` reads).
        // Reading it from anywhere else would let a subject name one broker while the send reaches another.
        // Empty when the load carries neither, so a template that quotes it degrades to "()" rather than to
        // a stale address from a different load.
        '{{broker_email}}': load.brokerContactEmail || load.posterContactEmail || '',
        // Broker credit info (DAT-sourced) lives on the `details` JSONB bag; null until surfaced.
        '{{credit_score}}': load.details?.credit_score != null ? String(load.details.credit_score) : 'N/A',
        '{{days_to_pay}}': load.details?.days_to_pay != null ? String(load.details.days_to_pay) : 'N/A',
        '{{driver_name}}': '',
        '{{driver_phone}}': '',
        // Broker's own posting reference (e.g. "SW188" / DAT "32261106") — the ONLY id shown to a broker.
        // `externalId` is our internal `SOURCE|rawId` dedup key and must NEVER be surfaced as a reference.
        '{{reference_id}}': load.postersReferenceId || '',
        ...buildUserReplacements(userCompanyData),
    }

    let result = template
    Object.entries(replacements).forEach(([key, value]) => {
        result = result.split(key).join(value)
    })

    return result
}

/**
 * Generate email content using user's template or fallback to default.
 * Includes the template's signature/footer when present.
 */
export const generateEmailContentFromTemplate = (
    load: CanonicalLoad,
    userName: string,
    template?: BodyEmailTemplate,
    userCompanyData?: UserCompanyData
): EmailContent => {
    const origin = [load.pickupCity, load.pickupStateProv].filter(Boolean).join(', ') || 'Unknown'
    const destination = [load.deliveryCity, load.deliveryStateProv].filter(Boolean).join(', ') || 'Unknown'

    // If user has a template, use it with variable interpolation
    if (template?.body && template?.subject) {
        let body = ensureHtmlLineBreaks(applyTemplateVariables(template.body, load, userCompanyData))
        const subject = applyTemplateVariables(template.subject, load, userCompanyData)

        // Append signature/footer from the template. The signature logo lives inline inside
        // the signature HTML (single source of truth) — no separate legacy URL is merged here.
        if (template.signature) {
            const resolvedSignature = ensureHtmlLineBreaks(applyTemplateVariables(template.signature, load, userCompanyData))
            if (resolvedSignature) {
                body = `${body}<br><br>${resolvedSignature}`
            }
        }

        return { subject, body }
    }

    // Fallback to default template
    return {
        subject: `Inquiry about load from ${origin}`,
        body: `Hi,<br><br>I saw your load from ${origin} to ${destination}. I have a truck available and interested in this load. Please let me know if it's still available.<br><br>Thanks,<br>${userName}`,
    }
}

/**
 * Convert plain-text newlines to HTML <br> tags.
 * If the body already contains HTML block-level tags, it's returned as-is.
 * This matches the extension's ensureHtmlLineBreaks behavior.
 */
export const ensureHtmlLineBreaks = (body: string): string => {
    if (/<(?:p|div|br|ul|ol|li|h[1-6]|table|tr|td|th)\b/i.test(body)) {
        return body
    }
    return body.replace(/\n/g, '<br>')
}
